import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, KeyRound, Lock, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine if this is advisor flow based on URL
  const isAdvisor = location.pathname.includes('advisor');
  
  // Step: 1 = enter email, 2 = enter OTP, 3 = new password
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Theme colors based on user type
  const theme = isAdvisor 
    ? {
        gradient: 'from-orange-500 to-red-600',
        gradientHover: 'from-orange-600 to-red-700',
        ring: 'ring-orange-500',
        border: 'border-orange-500',
        text: 'text-orange-400',
        textHover: 'text-orange-300',
        backLink: '/advisor/login',
        backText: 'Back to Advisor Login',
        logo: 'FA',
        subtitle: 'for Advisors'
      }
    : {
        gradient: 'from-teal-500 to-emerald-600',
        gradientHover: 'from-teal-600 to-emerald-700',
        ring: 'ring-teal-500',
        border: 'border-teal-500',
        text: 'text-teal-400',
        textHover: 'text-teal-300',
        backLink: '/login',
        backText: 'Back to Login',
        logo: 'F',
        subtitle: null
      };

  const validateEmail = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email is invalid';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};
    if (!otp) newErrors.otp = 'OTP is required';
    else if (otp.length !== 6) newErrors.otp = 'OTP must be 6 digits';
    else if (!/^\d+$/.test(otp)) newErrors.otp = 'OTP must contain only numbers';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!newPassword) newErrors.newPassword = 'Password is required';
    else if (newPassword.length < 6) newErrors.newPassword = 'Password must be at least 6 characters';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!validateEmail()) return;
    
    setLoading(true);
    setErrors({});

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('OTP sent to your email!');
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send OTP. Please try again.';
      toast.error(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!validateOtp()) return;
    
    setLoading(true);
    setErrors({});

    try {
      await axios.post(`${API}/auth/verify-otp`, { email, otp });
      toast.success('OTP verified!');
      setStep(3);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid OTP. Please try again.';
      toast.error(errorMsg);
      setErrors({ otp: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;
    
    setLoading(true);
    setErrors({});

    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email, 
        otp, 
        new_password: newPassword 
      });
      toast.success('Password reset successful!');
      // Navigate to login after short delay
      setTimeout(() => {
        navigate(isAdvisor ? '/advisor/login' : '/login');
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to reset password. Please try again.';
      toast.error(errorMsg);
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('New OTP sent to your email!');
      setOtp('');
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get title and subtitle based on step
  const getStepTitle = () => {
    if (step === 1) return 'Forgot Password?';
    if (step === 2) return 'Enter OTP';
    return 'Create New Password';
  };

  const getStepSubtitle = () => {
    if (step === 1) return "Enter your email and we'll send you an OTP";
    if (step === 2) return `We've sent a 6-digit code to ${email}`;
    return 'Choose a strong password for your account';
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to={theme.backLink}
          className="inline-flex items-center text-gray-400 hover:text-gray-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {theme.backText}
        </Link>

        {/* Card */}
        <div className="bg-gray-800/50 backdrop-blur-xl border border-gray-700 rounded-2xl p-8 shadow-2xl">
          {/* Header */}
          <div className="flex flex-col items-center mb-8">
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center mb-4 shadow-lg`}>
              <span className="text-white font-bold text-2xl">{theme.logo}</span>
            </div>
            {theme.subtitle && (
              <p className="text-sm text-gray-400 -mt-2 mb-2">{theme.subtitle}</p>
            )}
            <h1 className="text-2xl font-bold text-white mb-2">
              {getStepTitle()}
            </h1>
            <p className="text-gray-400 text-center text-sm">
              {getStepSubtitle()}
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                  step >= s 
                    ? `bg-gradient-to-br ${theme.gradient} text-white` 
                    : 'bg-gray-700 text-gray-400'
                }`}>
                  {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-8 h-1 mx-1 rounded ${step > s ? `bg-gradient-to-r ${theme.gradient}` : 'bg-gray-700'}`} />
                )}
              </div>
            ))}
          </div>

          {/* All Forms - Using CSS display instead of conditional rendering */}
          <div>
            {/* Step 1: Email Form */}
            <form 
              onSubmit={handleRequestOTP} 
              className="space-y-5"
              style={{ display: step === 1 ? 'block' : 'none' }}
            >
              <div>
                <Label htmlFor="email-input" className="text-gray-300 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email Address
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({});
                    }}
                    className={`h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 ${
                      errors.email ? 'border-red-500' : ''
                    }`}
                    placeholder="you@example.com"
                    data-testid="forgot-password-email-input"
                  />
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${theme.gradient} hover:opacity-90 text-white font-semibold h-12 rounded-lg`}
                data-testid="forgot-password-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending OTP...
                  </span>
                ) : (
                  'Send OTP'
                )}
              </Button>
            </form>

            {/* Step 2: OTP Form */}
            <form 
              onSubmit={handleVerifyOTP} 
              className="space-y-5"
              style={{ display: step === 2 ? 'block' : 'none' }}
            >
              <div>
                <Label htmlFor="otp-input" className="text-gray-300 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" />
                  Enter 6-Digit OTP
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="otp-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      setOtp(value);
                      setErrors({});
                    }}
                    className={`h-14 bg-gray-700 border-gray-600 text-white text-center text-2xl tracking-[0.5em] placeholder:text-gray-500 placeholder:tracking-normal placeholder:text-base ${
                      errors.otp ? 'border-red-500' : ''
                    }`}
                    placeholder="••••••"
                    data-testid="forgot-password-otp-input"
                  />
                  {errors.otp && (
                    <p className="text-red-400 text-sm mt-1 text-center">{errors.otp}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${theme.gradient} hover:opacity-90 text-white font-semibold h-12 rounded-lg`}
                data-testid="forgot-password-verify-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verifying...
                  </span>
                ) : (
                  'Verify OTP'
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className={`text-sm ${theme.text} hover:opacity-80 font-medium transition-colors`}
                  data-testid="forgot-password-resend-btn"
                >
                  Didn't receive the code? Resend OTP
                </button>
              </div>

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full text-sm text-gray-400 hover:text-gray-300 transition-colors"
              >
                ← Use different email
              </button>
            </form>

            {/* Step 3: New Password Form */}
            <form 
              onSubmit={handleResetPassword} 
              className="space-y-5"
              style={{ display: step === 3 ? 'block' : 'none' }}
            >
              <div>
                <Label htmlFor="newPassword-input" className="text-gray-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  New Password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="newPassword-input"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrors({});
                    }}
                    className={`h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 pr-10 ${
                      errors.newPassword ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter new password"
                    data-testid="forgot-password-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  {errors.newPassword && (
                    <p className="text-red-400 text-sm mt-1">{errors.newPassword}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="confirmPassword-input" className="text-gray-300 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Confirm Password
                </Label>
                <div className="relative mt-2">
                  <Input
                    id="confirmPassword-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors({});
                    }}
                    className={`h-12 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500 pr-10 ${
                      errors.confirmPassword ? 'border-red-500' : ''
                    }`}
                    placeholder="Confirm new password"
                    data-testid="forgot-password-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  {errors.confirmPassword && (
                    <p className="text-red-400 text-sm mt-1">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${theme.gradient} hover:opacity-90 text-white font-semibold h-12 rounded-lg`}
                data-testid="forgot-password-reset-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Resetting Password...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </div>

          {errors.submit && (
            <p className="text-red-400 text-sm mt-4 text-center">{errors.submit}</p>
          )}
        </div>
      </div>
    </div>
  );
}
